/**
 * @package   EGI / Frontend — cart
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.0 (M-EGI-385 passo 3)
 * @date      2026-07-05
 * @purpose   Contratti di tipo del modulo carrello (design ARCH_CART.md §4):
 *            il cart è ESTENSIONE dell'isola wishlist (ADR-C6) — la sezione
 *            `cart` vive dentro il blocco JSON #wishlist-config e viene
 *            validata QUI con il suo type-guard (il guard wishlist ignora le
 *            chiavi extra: retro-compatibile). Evento egi:cart:changed come da
 *            contratto §4. Zero `any` (tsconfig strict). Server-only: il cart
 *            NON ha modalità guest (ADR-C7).
 */

export interface CartEndpoints {
    /** Template POST move-to-cart T1/T1b — placeholder __ID__ (route api.egis.cart.add). */
    add: string;
    /** Template DELETE T2 (torna a wishlist) — placeholder __ID__ (route api.egis.cart.remove). */
    remove: string;
    /** GET lista con availability+prezzo live (route api.cart.index). */
    list: string;
    /** GET count badge (route api.cart.count). */
    count: string;
}

export interface CartLabels {
    added: string;
    removed: string;
    error: string;
    navLabel: string;
    /** Template con :count (risolto client-side — pattern wishlist §1.2). */
    navCount: string;
    countOne: string;
    /** Template con :count. */
    countOther: string;
}

export interface CartConfig {
    endpoints: CartEndpoints;
    labels: CartLabels;
}

const ENDPOINT_KEYS: ReadonlyArray<keyof CartEndpoints> = ['add', 'remove', 'list', 'count'];
const LABEL_KEYS: ReadonlyArray<keyof CartLabels> = [
    'added', 'removed', 'error', 'navLabel', 'navCount', 'countOne', 'countOther',
];

function isStringRecord(value: unknown, keys: ReadonlyArray<string>): boolean {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const record = value as Record<string, unknown>; // narrow post-guard: solo lettura chiavi
    return keys.every((key) => typeof record[key] === 'string');
}

/** Type-guard della sezione cart dentro #wishlist-config: malformata → modulo cart spento. */
export function isCartConfig(value: unknown): value is CartConfig {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const record = value as Record<string, unknown>;
    return isStringRecord(record.endpoints, ENDPOINT_KEYS) && isStringRecord(record.labels, LABEL_KEYS);
}

// ── Eventi DOM (contratto di integrazione — design ARCH_CART.md §4) ──────────

export const CART_CHANGED_EVENT = 'egi:cart:changed';

export type CartChangedAction = 'add' | 'remove' | 'sync';

export interface CartChangedDetail {
    count: number;
    id?: string;
    action: CartChangedAction;
}

/**
 * Refusal di dominio 422 al move (§2.3 — own_egi/not_published/not_available):
 * NON è un errore di sistema, NON passa da UEM (precedente P3-1 M-373).
 * Il message è già i18n-risolto dal server.
 */
export class CartRefusalError extends Error {
    public constructor(
        public readonly reason: string,
        message: string,
    ) {
        super(message);
        this.name = 'CartRefusalError';
    }
}

declare global {
    interface Window {
        /** API del modulo cart (gemella di window.egiWishlist — design §4). */
        egiCart?: { refresh: () => void };
    }
}
